// components/Form/FileUpload.js
import React, { useRef } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { Icon, LinearProgress } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";

function FileUpload({
  label,
  value,
  onChange,
  error,
  success,
  helperText,
  accept = "*",
  multiple = false,
  maxSize,
  maxFiles,
  disabled = false,
  uploading = false,
  uploadProgress = 0,
  showDownloadTemplate = false,
  downloadTemplateLabel = "Download Template",
  onDownloadTemplate = null,
}) {
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);

    if (!multiple && files.length > 0) {
      onChange(files[0]);
    } else if (multiple) {
      const currentFiles = value || [];
      const newFiles = [...currentFiles, ...files];
      if (maxFiles && newFiles.length > maxFiles) {
        // Handle max files exceeded
        return;
      }
      onChange(newFiles);
    }

    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveFile = (index) => {
    if (!multiple) {
      onChange(null);
    } else {
      const newFiles = [...value];
      newFiles.splice(index, 1);
      onChange(newFiles);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (filename) => {
    const ext = filename.split(".").pop().toLowerCase();
    switch (ext) {
      case "pdf":
        return "picture_as_pdf";
      case "doc":
      case "docx":
        return "description";
      case "jpg":
      case "jpeg":
      case "png":
        return "image";
      default:
        return "insert_drive_file";
    }
  };

  const renderFilePreview = () => {
    if (!value) return null;

    const files = multiple ? value : [value];

    return (
      <MDBox mt={2}>
        {files.map((file, index) => (
          <MDBox
            key={index}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            p={1}
            mb={1}
            sx={{
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              border: "1px solid #e0e0e0",
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon fontSize="small">{getFileIcon(file.name)}</Icon>
              <MDBox>
                <MDTypography variant="caption" display="block">
                  {file.name}
                </MDTypography>
                <MDTypography variant="caption" color="text">
                  {formatFileSize(file.size)}
                </MDTypography>
              </MDBox>
            </MDBox>
            {!disabled && (
              <MDButton
                variant="text"
                color="error"
                size="small"
                onClick={() => handleRemoveFile(index)}
              >
                <DeleteIcon fontSize="small" />
              </MDButton>
            )}
          </MDBox>
        ))}
      </MDBox>
    );
  };

  return (
    <MDBox width="100%">
      <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <MDTypography variant="body2" fontWeight="medium" color="text">
          {label}
        </MDTypography>
        {showDownloadTemplate && onDownloadTemplate && (
          <MDButton
            variant="text"
            color="info"
            size="small"
            onClick={onDownloadTemplate}
            startIcon={<DownloadIcon fontSize="small" />}
          >
            {downloadTemplateLabel}
          </MDButton>
        )}
      </MDBox>

      <MDBox
        sx={{
          border: error ? "1px solid #f44336" : "1px dashed #cccccc",
          borderRadius: "4px",
          p: 2,
          backgroundColor: disabled ? "#f5f5f5" : "#fafafa",
          cursor: disabled ? "not-allowed" : "pointer",
          textAlign: "center",
          transition: "all 0.3s ease",
          "&:hover": disabled
            ? {}
            : {
                borderColor: "#0288d1",
                backgroundColor: "#f0f7ff",
              },
        }}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileSelect}
          style={{ display: "none" }}
          disabled={disabled}
        />
        <CloudUploadIcon sx={{ fontSize: 48, color: error ? "#f44336" : "#0288d1", mb: 1 }} />
        <MDTypography variant="body2" color={error ? "error" : "text"}>
          Click to upload {multiple ? "files" : "a file"}
        </MDTypography>
        <MDTypography variant="caption" color="text">
          {helperText || `Supported formats: ${accept}`}
        </MDTypography>
        {uploading && (
          <MDBox mt={2}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <MDTypography variant="caption" color="text" mt={1}>
              Uploading: {uploadProgress}%
            </MDTypography>
          </MDBox>
        )}
      </MDBox>

      {renderFilePreview()}

      {helperText && !error && (
        <MDTypography variant="caption" color="text" sx={{ mt: 0.5, display: "block" }}>
          {helperText}
        </MDTypography>
      )}

      {error && (
        <MDTypography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
          {error}
        </MDTypography>
      )}
    </MDBox>
  );
}

FileUpload.propTypes = {
  label: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  success: PropTypes.bool,
  helperText: PropTypes.string,
  accept: PropTypes.string,
  multiple: PropTypes.bool,
  maxSize: PropTypes.number,
  maxFiles: PropTypes.number,
  disabled: PropTypes.bool,
  uploading: PropTypes.bool,
  uploadProgress: PropTypes.number,
  showDownloadTemplate: PropTypes.bool,
  downloadTemplateLabel: PropTypes.string,
  onDownloadTemplate: PropTypes.func,
};

export default FileUpload;
